import React, { useState } from 'react';
import {
  ThemeProvider,
  createTheme,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Chip,
  Box,
  Card,
  CardContent,
  Grid,
  AppBar,
  Toolbar,
  IconButton,
  useScrollTrigger,
  Slide,
  Fab,
  Zoom,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Search,
  KeyboardArrowUp,
  TrendingUp,
  TrendingDown,
  Remove,
  HealthAndSafety,
  Equalizer,
  FilterList,
} from '@mui/icons-material';

// NavigationBar Component
function NavigationBar() {
  return (
    <AppBar position="fixed" sx={{ backgroundColor: '#0d933a' }}>
      <Toolbar>
        <HealthAndSafety sx={{ mr: 2 }} />
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          MSPLS - Suivi des Indicateurs de la Vision
        </Typography>
      </Toolbar>
    </AppBar>
  );
}

// Footer Component
function Footer() {
  return (
    <Box sx={{ backgroundColor: '#0d933a', color: '#fefffe', py: 3, mt: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="body2" align="center">
          © 2024 Ministère de la Santé Publique et de la Lutte Sociale
        </Typography>
      </Container>
    </Box>
  );
}

// Scroll to top component
function ScrollTop(props) {
  const { children } = props;
  const theme = useTheme();
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = (event) => {
    const anchor = (event.target.ownerDocument || document).querySelector(
      '#back-to-top-anchor',
    );
    if (anchor) {
      anchor.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  return (
    <Zoom in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
      >
        {children}
      </Box>
    </Zoom>
  );
}

// Create theme with your colors
const theme = createTheme({
  palette: {
    primary: {
      main: '#0d933a', // Vert
    },
    secondary: {
      main: '#e30b14', // Rouge
    },
    background: {
      default: '#fefffe', // Blanc
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

// Sample data basée sur votre structure
const indicateursData = [
  {
    id: '12.3.5',
    pilier: 'Pilier 3: Equité sociale',
    axe: 'Axe 2: Développement du capital humain et inclusion sociale',
    objectif: 'O.S 2: Améliorer les services sociaux de base',
    projet: 'Projet de santé de la reproduction, maternelle, néonatale, infantile et des adolescents/adolescentes',
    indicateur: '12.3.5. Nombre de décès maternels dans les FOSA',
    unite: 'Nombre',
    structure: 'MSPLS',
    source: 'Données administratives',
    desagregation: 'National',
    desagrege: 'Non Désegregé',
    disponible: 'Oui',
    reference2022: 360,
    valeur2022: 360,
    cible2023: 345,
    valeur2023: 365,
  },
  {
    id: '11.1.1',
    pilier: 'Pilier 3: Equité sociale',
    axe: 'Axe 2: Développement du capital humain et inclusion sociale',
    objectif: 'O.S 2: Améliorer les services sociaux de base',
    projet: 'Projet d\'amélioration d\'accès aux services de santé primaires et de pointe',
    indicateur: '11.1.1. Prévalence de la malnutrition aiguë globale chez les enfants de moins de 5 ans',
    unite: 'Pourcentage',
    structure: 'MSPLS',
    source: 'Enquête',
    desagregation: 'Par sexe',
    desagrege: 'Non Désagregé',
    disponible: 'Oui',
    reference2022: 0.05,
    valeur2022: 0.048,
    cible2023: 0.05,
    valeur2023: 0.047,
  },
  {
    id: '11.1.2',
    pilier: 'Pilier 3: Equité sociale',
    axe: 'Axe 2: Développement du capital humain et inclusion sociale',
    objectif: 'O.S 2: Améliorer les services sociaux de base',
    projet: 'Projet d\'amélioration d\'accès aux services de santé primaires et de pointe',
    indicateur: '11.1.2. Proportion de nouveau-nés mis au sein dans l\'heure qui a suivi la naissance',
    unite: 'Pourcentage',
    structure: 'MSPLS',
    source: 'Données administratives',
    desagregation: 'National',
    desagrege: 'Non désagreable',
    disponible: 'Oui',
    reference2022: 0.9,
    valeur2022: 90.1,
    cible2023: 90,
    valeur2023: 85.99,
  },
  // Ajoutez les autres indicateurs suivant la même structure...
];

function SuiviIndicateurs() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPilier, setSelectedPilier] = useState('');
  const [selectedAxe, setSelectedAxe] = useState('');
  const [selectedProjet, setSelectedProjet] = useState('');

  // Extraction des valeurs uniques pour les filtres
  const piliers = [...new Set(indicateursData.map(item => item.pilier))];
  const axes = [...new Set(indicateursData.map(item => item.axe))];
  const projets = [...new Set(indicateursData.map(item => item.projet))];

  const filteredIndicateurs = indicateursData.filter(indicateur => {
    const matchesSearch = 
      indicateur.indicateur.toLowerCase().includes(searchTerm.toLowerCase()) ||
      indicateur.id.includes(searchTerm);
    const matchesPilier = selectedPilier ? indicateur.pilier === selectedPilier : true;
    const matchesAxe = selectedAxe ? indicateur.axe === selectedAxe : true;
    const matchesProjet = selectedProjet ? indicateur.projet === selectedProjet : true;

    return matchesSearch && matchesPilier && matchesAxe && matchesProjet;
  });

  const calculateTendance = (valeur2022, valeur2023, cible2023) => {
    if (valeur2023 === undefined || valeur2023 === null) return 'inconnu';
    if (valeur2022 === undefined || valeur2022 === null) return 'inconnu';
    
    const evolution = ((valeur2023 - valeur2022) / valeur2022) * 100;
    const atteinteCible = valeur2023 >= cible2023;

    if (evolution > 0) return 'hausse';
    if (evolution < 0) return 'baisse';
    return 'stable';
  };

  const getTendanceIcon = (tendance) => {
    switch (tendance) {
      case 'hausse':
        return <TrendingUp sx={{ color: '#e30b14' }} />;
      case 'baisse':
        return <TrendingDown sx={{ color: '#0d933a' }} />;
      case 'stable':
        return <Remove sx={{ color: 'gray' }} />;
      default:
        return <Equalizer sx={{ color: 'gray' }} />;
    }
  };

  const getTendanceColor = (tendance) => {
    switch (tendance) {
      case 'hausse':
        return '#e30b14';
      case 'baisse':
        return '#0d933a';
      case 'stable':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const formatValue = (value, unite) => {
    if (value === undefined || value === null) return 'ND';
    if (unite === 'Pourcentage' && value < 1) return `${(value * 100).toFixed(1)}%`;
    if (unite === 'Pourcentage') return `${value}%`;
    return value;
  };

  return (
    <ThemeProvider theme={theme}>
      <div style={{ backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
        <NavigationBar />
        <div id="back-to-top-anchor" />
        
        <Container maxWidth="xl" style={{ paddingTop: '80px', paddingBottom: '32px' }}>
          {/* En-tête */}
          <Card sx={{ mb: 3, backgroundColor: '#f8f9fa' }}>
            <CardContent>
              <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#0d933a', fontWeight: 'bold' }}>
                Tableau de Bord des Indicateurs MSPLS
              </Typography>
              <Typography variant="h6" sx={{ color: '#e30b14' }}>
                Vision Nationale - Suivi des Performances
              </Typography>
              <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
                Pilier 3: Équité sociale - Axe 2: Développement du capital humain et inclusion sociale
              </Typography>
            </CardContent>
          </Card>

          {/* Filtres et recherche */}
          <Card sx={{ mb: 3, p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Rechercher un indicateur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Pilier</InputLabel>
                  <Select
                    value={selectedPilier}
                    onChange={(e) => setSelectedPilier(e.target.value)}
                    label="Pilier"
                  >
                    <MenuItem value="">Tous les piliers</MenuItem>
                    {piliers.map((pilier, index) => (
                      <MenuItem key={index} value={pilier}>
                        {pilier}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Axe</InputLabel>
                  <Select
                    value={selectedAxe}
                    onChange={(e) => setSelectedAxe(e.target.value)}
                    label="Axe"
                  >
                    <MenuItem value="">Tous les axes</MenuItem>
                    {axes.map((axe, index) => (
                      <MenuItem key={index} value={axe}>
                        {axe}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Projet</InputLabel>
                  <Select
                    value={selectedProjet}
                    onChange={(e) => setSelectedProjet(e.target.value)}
                    label="Projet"
                  >
                    <MenuItem value="">Tous les projets</MenuItem>
                    {projets.map((projet, index) => (
                      <MenuItem key={index} value={projet}>
                        {projet.length > 50 ? projet.substring(0, 50) + '...' : projet}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Card>

          {/* Tableau des indicateurs */}
          <TableContainer component={Paper} elevation={3} sx={{ maxHeight: '600px', overflow: 'auto' }}>
            <Table stickyHeader sx={{ minWidth: 1200 }} aria-label="tableau des indicateurs">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#0d933a', color: '#fefffe', fontWeight: 'bold', minWidth: 120 }}>PILIER</TableCell>
                  <TableCell sx={{ backgroundColor: '#0d933a', color: '#fefffe', fontWeight: 'bold', minWidth: 150 }}>AXES DU PND</TableCell>
                  <TableCell sx={{ backgroundColor: '#0d933a', color: '#fefffe', fontWeight: 'bold', minWidth: 120 }}>OBJECTIFS STRATEGIQUES</TableCell>
                  <TableCell sx={{ backgroundColor: '#0d933a', color: '#fefffe', fontWeight: 'bold', minWidth: 200 }}>PROJET</TableCell>
                  <TableCell sx={{ backgroundColor: '#0d933a', color: '#fefffe', fontWeight: 'bold', minWidth: 250 }}>INDICATEURS</TableCell>
                  <TableCell sx={{ backgroundColor: '#0d933a', color: '#fefffe', fontWeight: 'bold', minWidth: 100 }}>UNITE</TableCell>
                  <TableCell sx={{ backgroundColor: '#0d933a', color: '#fefffe', fontWeight: 'bold', minWidth: 100 }}>SOURCE</TableCell>
                  <TableCell sx={{ backgroundColor: '#0d933a', color: '#fefffe', fontWeight: 'bold', minWidth: 100 }}>DESAGREGATION</TableCell>
                  <TableCell sx={{ backgroundColor: '#0d933a', color: '#fefffe', fontWeight: 'bold', minWidth: 80 }}>2022</TableCell>
                  <TableCell sx={{ backgroundColor: '#0d933a', color: '#fefffe', fontWeight: 'bold', minWidth: 80 }}>Cible 2023</TableCell>
                  <TableCell sx={{ backgroundColor: '#0d933a', color: '#fefffe', fontWeight: 'bold', minWidth: 80 }}>2023</TableCell>
                  <TableCell sx={{ backgroundColor: '#0d933a', color: '#fefffe', fontWeight: 'bold', minWidth: 100 }}>TENDANCE</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredIndicateurs.map((indicateur) => {
                  const tendance = calculateTendance(
                    indicateur.valeur2022, 
                    indicateur.valeur2023, 
                    indicateur.cible2023
                  );
                  
                  return (
                    <TableRow
                      key={indicateur.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {indicateur.pilier}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {indicateur.axe}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {indicateur.objectif}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {indicateur.projet}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Chip 
                            label={indicateur.id} 
                            size="small" 
                            sx={{ backgroundColor: '#0d933a', color: '#fefffe', mb: 0.5 }}
                          />
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {indicateur.indicateur.replace(`${indicateur.id}. `, '')}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={indicateur.unite} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {indicateur.source}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={indicateur.desagregation} 
                          size="small" 
                          variant="outlined"
                          color={indicateur.desagrege.includes('Non') ? 'default' : 'primary'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {formatValue(indicateur.valeur2022, indicateur.unite)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#0d933a' }}>
                          {formatValue(indicateur.cible2023, indicateur.unite)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 'bold', 
                          color: indicateur.valeur2023 >= indicateur.cible2023 ? '#0d933a' : '#e30b14'
                        }}>
                          {formatValue(indicateur.valeur2023, indicateur.unite)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getTendanceIcon(tendance)}
                          <Typography 
                            variant="body2" 
                            sx={{ color: getTendanceColor(tendance) }}
                          >
                            {tendance}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Résumé */}
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ color: '#0d933a', fontWeight: 'bold' }}>
                    {filteredIndicateurs.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Indicateurs affichés
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ color: '#0d933a', fontWeight: 'bold' }}>
                    {projets.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Projets
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ color: '#0d933a', fontWeight: 'bold' }}>
                    {piliers.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Piliers
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ color: '#0d933a', fontWeight: 'bold' }}>
                    {axes.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Axes stratégiques
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>

        <ScrollTop>
          <Fab size="small" aria-label="scroll back to top" sx={{ backgroundColor: '#0d933a', color: '#fefffe' }}>
            <KeyboardArrowUp />
          </Fab>
        </ScrollTop>
      </div>
      <Footer />
    </ThemeProvider>
  );
}

export default SuiviIndicateurs;